import * as React from 'react'
import { Tooltip as BaseTooltip } from '@base-ui-components/react/tooltip'
import styles from './index.module.css'
import cx from 'classnames'

interface TooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  alignOffset?: number
  delay?: number
  closeDelay?: number
  arrow?: boolean
  dark?: boolean
  open?: boolean
  defaultOpen?: boolean
  // eslint-disable-next-line no-unused-vars
  onOpenChange?: (open: boolean) => void
  className?: string
  dataScreenshotsId?: string
}

/**
 * Tooltip para mostrar información contextual al hacer hover o focus.
 *
 * @example
 * ```tsx
 * // Uso básico
 * <Tooltip content="Ayuda contextual">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * // Con flecha y posicionamiento
 * <Tooltip content="Guardar cambios" side="bottom" arrow>
 *   <IconButton icon="save" />
 * </Tooltip>
 *
 * // Modo oscuro
 * <Tooltip content="Tooltip oscuro" dark>
 *   <button>Dark tooltip</button>
 * </Tooltip>
 *
 * // Con delay personalizado
 * <Tooltip content="Aparece rápido" delay={200}>
 *   <span>Info</span>
 * </Tooltip>
 *
 * // Controlado
 * const [open, setOpen] = useState(false)
 * <Tooltip content="Controlado" open={open} onOpenChange={setOpen}>
 *   <button>Click</button>
 * </Tooltip>
 * ```
 *
 * @see {@link https://base-ui.com/react/components/tooltip | Base UI Tooltip docs}
 */
export function Tooltip({
  children,
  content,
  side = 'top',
  align = 'center',
  sideOffset = 8,
  alignOffset = 0,
  delay = 600,
  closeDelay = 0,
  arrow = true,
  dark = false,
  open,
  defaultOpen = false,
  onOpenChange,
  className,
  dataScreenshotsId,
}: TooltipProps) {
  return (
    <BaseTooltip.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delay={delay}
      closeDelay={closeDelay}
    >
      <BaseTooltip.Trigger className={className}>
        {children}
      </BaseTooltip.Trigger>
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className={styles.TooltipPositioner}
        >
          <BaseTooltip.Popup
            className={cx(className, styles.TooltipContent, {
              [styles.dark]: dark,
            })}
            data-screenshots-id={dataScreenshotsId}
          >
            {arrow && (
              <BaseTooltip.Arrow className={styles.TooltipArrow}>
                <ArrowSvg />
              </BaseTooltip.Arrow>
            )}
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}

function ArrowSvg(props: React.ComponentProps<'svg'>) {
  return (
    <svg width="20" height="10" viewBox="0 0 20 10" fill="none" {...props}>
      <path
        d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z"
        className={styles.TooltipArrowFill}
      />
      <path
        d="M8.99542 1.85876C9.75604 1.17425 10.9106 1.17422 11.6713 1.85878L16.5281 6.22989C17.0789 6.72568 17.7938 7.00001 18.5349 7.00001L15.89 7L11.0023 2.60207C10.622 2.2598 10.0447 2.2598 9.66436 2.60207L4.77734 7L2.13171 7.00001C2.87284 7.00001 3.58774 6.72568 4.13861 6.22989L8.99542 1.85876Z"
        className={styles.TooltipArrowOuterStroke}
      />
      <path
        d="M10.3333 3.34539L5.47654 7.71648C4.55842 8.54279 3.36693 9 2.13172 9H0V8H2.13172C3.11989 8 4.07308 7.63423 4.80758 6.97318L9.66437 2.60207C10.0447 2.25979 10.622 2.2598 11.0023 2.60207L15.8591 6.97318C16.5936 7.63423 17.5468 8 18.5349 8H20V9H18.5349C17.2998 9 16.1083 8.54278 15.1901 7.71648L10.3333 3.34539Z"
        className={styles.TooltipArrowInnerStroke}
      />
    </svg>
  )
}
